import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, UserCheck, MessageSquare, Star, ShoppingBag, TrendingUp, ArrowRight, X, RefreshCw, DollarSign, Moon, Sun } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { useTheme } from '../../context/ThemeContext';
import ProficiencyIconsManager from '../../components/admin/ProficiencyIconsManager';
import TrainerListManagerNew from '../../components/admin/TrainerListManagerNew';
import CustomerListManager from '../../components/admin/CustomerListManager';
import UsersManager from '../../components/admin/UsersManager';
import FooterManager from '../../components/admin/FooterManager';
import DenemePageManager from '../../components/admin/DenemePageManager';
import ContactInfoManager from '../../components/admin/ContactInfoManager';
import SupportTicketsManager from '../../components/admin/SupportTicketsManager';
import AboutUsManager from '../../components/admin/AboutUsManager';
import ReportedMessagesManager from '../../components/admin/ReportedMessagesManager';
import ReportedTrainersManager from '../../components/admin/ReportedTrainersManager';
import TrainerReportWarningsManager from '../../components/admin/TrainerReportWarningsManager';
import RestockRequestsManager from '../../components/admin/RestockRequestsManager';
import IPLogsManager from '../../components/admin/IPLogsManager';
import ReviewsManager from '../../components/admin/ReviewsManager';
import BlogManager from '../../components/admin/BlogManager';
import CommentManager from '../../components/admin/CommentManager';
import NewsletterManager from '../../components/admin/NewsletterManager';
import FeaturedVideoManager from '../../components/admin/FeaturedVideoManager';
import ShopManager from '../../components/admin/ShopManager';
import PrivacyPolicyManager from '../../components/admin/PrivacyPolicyManager';
import LegalDisclaimerManager from '../../components/admin/LegalDisclaimerManager';
import PaymentInfoManager from '../../components/admin/PaymentInfoManager';
import TermsConditionsManager from '../../components/admin/TermsConditionsManager';
import DisclaimerManager from '../../components/admin/DisclaimerManager';
import EmailTemplatesManager from '../../components/admin/EmailTemplatesManager';
import SocialMediaManager from '../../components/admin/SocialMediaManager';
import MembershipsManager from '../../components/admin/MembershipsManager';
import BoostCategoriesManager from '../../components/admin/BoostCategoriesManager';
import AchievementsManager from '../../components/admin/AchievementsManager';
import ReferralManager from '../../components/admin/ReferralManager';
import ListingCreditsManager from '../../components/admin/ListingCreditsManager';
import CreditPackagesManager from '../../components/admin/CreditPackagesManager';
import CouponManager from '../../components/admin/CouponManager';
import ProficiencySettingManager from '../../components/admin/ProficiencySettingManager';
import TypeSettingManager from '../../components/admin/TypeSettingManager';
import SiteSettingsManager from '../../components/admin/SiteSettingsManager';
import PageSettingsManager from '../../components/admin/PageSettingsManager';
import CommunityPageManager from '../../components/admin/CommunityPageManager';
import GoogleTagManager from '../../components/admin/GoogleTagManager';
import CitiesManager from '../../components/admin/CitiesManager';
import CountriesManager from '../../components/admin/CountriesManager';
import PaymentHistory from '../../components/admin/PaymentHistory';
import AdminPayments from '../../components/admin/AdminPayments';
import AdvertisingInquiriesManager from '../../components/admin/AdvertisingInquiriesManager';
import AdvertisementsManager from '../../components/admin/AdvertisementsManager';
import FAQManager from '../../components/admin/FAQManager';
import AdminAccountSettings from '../../components/admin/AdminAccountSettings';

const AdminDashboardNew = ({ defaultTab }) => {
  const [currentSection, setCurrentSection] = useState(defaultTab || 'dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar state
  const [highlightUserId, setHighlightUserId] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrainers: 0,
    pendingTrainers: 0,
    totalReviews: 0,
    reportedMessages: 0,
    totalOrders: 0
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Check for URL query parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const highlight = params.get('highlight');
    
    if (tab) {
      setCurrentSection(tab);
    } else if (defaultTab) {
      // Use defaultTab prop if no query param
      setCurrentSection(defaultTab);
    }
    
    if (highlight) {
      setHighlightUserId(highlight);
    }
  }, [defaultTab]);

  useEffect(() => {
    // Fetch stats when dashboard loads
    if (currentSection === 'dashboard') {
      fetchStats();
    }
  }, [currentSection]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      
      const response = await fetch(`${API_BASE}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback to zeros if API fails
      setStats({
        totalCustomers: 0,
        customersToday: 0,
        customersMonth: 0,
        totalTrainers: 0,
        trainersToday: 0,
        trainersMonth: 0,
        totalOrders: 0,
        ordersToday: 0,
        ordersMonth: 0,
        totalEarnings: 0,
        earningsToday: 0,
        earningsMonth: 0
      });
    }
  };

  const renderContent = () => {
    switch (currentSection) {
      case 'dashboard':
        return <DashboardOverview stats={stats} />;
      case 'trainers':
        return <TrainerListManagerNew />;
      case 'customers':
        return <CustomerListManager />;
      case 'reported-messages':
        return <ReportedMessagesManager 
          onNavigateToIPLogs={(userId) => {
            setCurrentSection('ip-logs');
            setHighlightUserId(userId);
          }} 
        />;
      case 'reported-trainers':
        return <ReportedTrainersManager />;
      case 'trainer-warnings':
        return <TrainerReportWarningsManager />;
      case 'ip-logs':
        return <IPLogsManager highlightUserId={highlightUserId} onHighlightComplete={() => setHighlightUserId(null)} />;
      case 'support-tickets':
        return <SupportTicketsManager />;
      case 'advertising-inquiries':
        return <AdvertisingInquiriesManager />;
      case 'advertisements':
        return <AdvertisementsManager />;
      case 'faq':
        return <FAQManager />;
      case 'contact-info':
        return <ContactInfoManager />;
      case 'about-us':
        return <AboutUsManager />;
      case 'reviews':
        return <ReviewsManager />;
      case 'blog':
        return <BlogManager />;
      case 'comments':
        return <CommentManager />;
      case 'newsletter':
        return <NewsletterManager />;
      case 'featured-video':
        return <FeaturedVideoManager />;
      case 'proficiency-icons':
        return <ProficiencyIconsManager />;
      case 'shop':
        return <ShopManager />;
      case 'restock-requests':
        return <RestockRequestsManager />;
      case 'footer':
        return <FooterManager />;
      case 'deneme-page':
        return <DenemePageManager />;
      case 'privacy-policy':
        return <PrivacyPolicyManager />;
      case 'legal-disclaimer':
        return <LegalDisclaimerManager />;
      case 'payment-info':
        return <PaymentInfoManager />;
      case 'terms-conditions':
        return <TermsConditionsManager />;
      case 'disclaimer':
        return <DisclaimerManager />;
      case 'email-templates':
        return <EmailTemplatesManager />;
      case 'social-media':
        return <SocialMediaManager />;
      case 'memberships':
        return <MembershipsManager />;
      case 'boost-categories':
        return <BoostCategoriesManager />;
      case 'achievements':
        return <AchievementsManager />;
      case 'referrals':
        return <ReferralManager />;
      case 'listing-credits':
        return <ListingCreditsManager />;
      case 'credit-packages':
        return <CreditPackagesManager />;
      case 'coupons':
        return <CouponManager />;
      case 'proficiency-setting':
        return <ProficiencySettingManager />;
      case 'type-setting':
        return <TypeSettingManager />;
      case 'page-settings':
        return <PageSettingsManager />;
      case 'community-page':
        return <CommunityPageManager />;
      case 'settings':
        return <SiteSettingsManager />;
      case 'google-tag-manager':
        return <GoogleTagManager />;
      case 'city-settings':
        return <CitiesManager />;
      case 'country-settings':
        return <CountriesManager />;
      case 'payment-history':
        return <PaymentHistory />;
      case 'payments':
        return <AdminPayments />;
      case 'account-settings':
        return <AdminAccountSettings />;
      default:
        return <DashboardOverview stats={stats} />;
    }
  };

  const { theme, toggleTheme } = useTheme();

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      <AdminSidebar 
        currentSection={currentSection} 
        onSectionChange={(section) => {
          setCurrentSection(section);
          setIsSidebarOpen(false); // Close sidebar on mobile after selection
        }} 
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      
      <div className="flex-1 overflow-auto w-full">
        {/* Mobile menu button */}
        <div className="lg:hidden sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between p-4">
            <h2 className="font-bold text-lg text-gray-900 dark:text-white">Admin Panel</h2>
            <div className="flex items-center gap-2">
              {/* Theme Toggle Button - Mobile */}
              <button
                onClick={toggleTheme}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors active:scale-95"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {renderContent()}
      </div>
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = ({ stats }) => {
  const [recentActivities, setRecentActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [showAllModal, setShowAllModal] = useState(false);
  const [loadingAllActivities, setLoadingAllActivities] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const fetchRecentActivities = useCallback(async (silent = false) => {
    if (!silent) {
      setLoadingActivities(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Get only the 5 most recent
        setRecentActivities(data.slice(0, 5));
      } else {
        const errorText = await response.text();
        console.error('[RECENT ACTIVITY] Error response:', errorText);
      }
    } catch (error) {
      console.error('[RECENT ACTIVITY] Error fetching activities:', error);
    } finally {
      if (!silent) {
        setLoadingActivities(false);
      }
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchRecentActivities();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchRecentActivities(true); // Silent refresh
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchRecentActivities]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchRecentActivities(false);
    setRefreshing(false);
  };

  const fetchAllActivities = async () => {
    setLoadingAllActivities(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllActivities(data); // Get all activities
      }
    } catch (error) {
      console.error('Error fetching all activities:', error);
    } finally {
      setLoadingAllActivities(false);
    }
  };

  const handleShowAllActivities = () => {
    setShowAllModal(true);
    if (allActivities.length === 0) {
      fetchAllActivities();
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'new_trainer':
        return { Icon: UserCheck, bg: 'bg-blue-100', color: 'text-blue-600' };
      case 'trainer_approved':
        return { Icon: UserCheck, bg: 'bg-green-100', color: 'text-green-600' };
      case 'trainer_rejected':
        return { Icon: UserCheck, bg: 'bg-red-100', color: 'text-red-600' };
      case 'trainer_deleted':
        return { Icon: X, bg: 'bg-gray-100', color: 'text-gray-600' };
      case 'listing_request':
        return { Icon: TrendingUp, bg: 'bg-blue-100', color: 'text-blue-600' };
      case 'listing_approved':
        return { Icon: TrendingUp, bg: 'bg-green-100', color: 'text-green-600' };
      case 'listing_rejected':
        return { Icon: TrendingUp, bg: 'bg-red-100', color: 'text-red-600' };
      case 'listing_changes_requested':
        return { Icon: TrendingUp, bg: 'bg-orange-100', color: 'text-orange-600' };
      case 'message_reported':
        return { Icon: MessageSquare, bg: 'bg-red-100', color: 'text-red-600' };
      default:
        return { Icon: Users, bg: 'bg-gray-100', color: 'text-gray-600' };
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'recently';
    
    // Backend now sends proper ISO format with timezone
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  const statCards = [
    // Row 1: Today's metrics
    {
      title: 'Customer Registered Today',
      value: stats.customersToday || 0,
      icon: Users,
      color: 'bg-blue-500',
      iconBg: 'bg-blue-100'
    },
    {
      title: 'Trainers Registered Today',
      value: stats.trainersToday || 0,
      icon: UserCheck,
      color: 'bg-orange-500',
      iconBg: 'bg-orange-100'
    },
    {
      title: "Today's Earnings",
      value: `${(stats.earningsToday || 0).toFixed(2)} CAD $`,
      icon: DollarSign,
      color: 'bg-green-500',
      iconBg: 'bg-green-100'
    },
    // Row 2: Monthly metrics
    {
      title: 'Customer Registered Month',
      value: stats.customersMonth || 0,
      icon: Users,
      color: 'bg-blue-500',
      iconBg: 'bg-blue-100'
    },
    {
      title: 'Trainers Registered Month',
      value: stats.trainersMonth || 0,
      icon: UserCheck,
      color: 'bg-orange-500',
      iconBg: 'bg-orange-100'
    },
    {
      title: "This Month's Earnings",
      value: `${(stats.earningsMonth || 0).toFixed(2)} CAD $`,
      icon: DollarSign,
      color: 'bg-green-500',
      iconBg: 'bg-green-100'
    },
    // Row 3: Total metrics
    {
      title: 'All Customers',
      value: stats.totalCustomers || 0,
      icon: Users,
      color: 'bg-blue-500',
      iconBg: 'bg-blue-100'
    },
    {
      title: 'All Trainers',
      value: stats.totalTrainers || 0,
      icon: UserCheck,
      color: 'bg-orange-500',
      iconBg: 'bg-orange-100'
    },
    {
      title: 'All Earnings',
      value: `${(stats.totalEarnings || 0).toFixed(2)} CAD $`,
      icon: DollarSign,
      color: 'bg-green-500',
      iconBg: 'bg-green-100'
    },
    // Row 4: Orders
    {
      title: "Today's Orders",
      value: stats.ordersToday || 0,
      icon: ShoppingBag,
      color: 'bg-red-500',
      iconBg: 'bg-red-100'
    },
    {
      title: "This Month's Orders",
      value: stats.ordersMonth || 0,
      icon: ShoppingBag,
      color: 'bg-red-500',
      iconBg: 'bg-red-100'
    },
    {
      title: 'All Orders',
      value: stats.totalOrders || 0,
      icon: ShoppingBag,
      color: 'bg-red-500',
      iconBg: 'bg-red-100'
    }
  ];

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Dashboard Overview</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">Welcome back! Here's what's happening today.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="overflow-hidden border border-gray-200 dark:border-gray-700 dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  </div>
                  <div className={`${stat.iconBg} dark:bg-opacity-20 p-3 rounded-lg`}>
                    <Icon className={`w-8 h-8 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <Card className="mt-6 dark:bg-gray-800 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="dark:text-white">Recent Activity</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="h-8 w-8 p-0 dark:hover:bg-gray-700"
              title="Refresh activities"
            >
              <RefreshCw className={`w-4 h-4 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingActivities ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 dark:text-gray-400">Loading recent activities...</div>
            </div>
          ) : recentActivities.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">No recent activities</div>
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity, index) => {
                const { Icon, bg, color } = getActivityIcon(activity.type);
                return (
                  <div key={activity.id || `${activity.type}-${activity.created_at}-${index}`} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className={`w-10 h-10 rounded-full ${bg} dark:bg-opacity-20 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium dark:text-white">{activity.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{activity.message}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(activity.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* View All Button */}
          {!loadingActivities && recentActivities.length > 0 && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={handleShowAllActivities}
                className="w-full sm:w-auto flex items-center gap-2 hover:bg-red-50 hover:border-red-300 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:border-gray-500"
              >
                View All Activities
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Activities Modal */}
      <Dialog open={showAllModal} onOpenChange={setShowAllModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col dark:bg-gray-800 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-2xl dark:text-white">All Activities</DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              Complete history of all system activities
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {loadingAllActivities ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500 dark:text-gray-400">Loading activities...</div>
              </div>
            ) : allActivities.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500 dark:text-gray-400">No activities yet</div>
              </div>
            ) : (
              <div className="space-y-3">
                {allActivities.map((activity, index) => {
                  const { Icon, bg, color } = getActivityIcon(activity.type);
                  return (
                    <div key={activity.id || `${activity.type}-${activity.created_at}-${index}`} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors">
                      <div className={`w-10 h-10 rounded-full ${bg} dark:bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">{activity.title}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 break-words">{activity.message}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(activity.created_at)}</p>
                          {activity.created_at && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {new Date(activity.created_at).toLocaleString('tr-TR')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-4 border-t dark:border-gray-700">
            <Button onClick={() => setShowAllModal(false)} variant="outline" className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Messages Section Component
const MessagesSection = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/messages/reports?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (reportId, action) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/messages/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: action,
          admin_notes: `Report ${action} by admin`
        })
      });

      if (response.ok) {
        fetchReports(); // Refresh list
      }
    } catch (error) {
      console.error('Error resolving report:', error);
    }
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 dark:text-white">Reported Messages</h2>

      {loading ? (
        <p className="text-sm sm:text-base dark:text-gray-400">Loading reports...</p>
      ) : reports.length === 0 ? (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="p-8 sm:p-12 text-center">
            <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-base sm:text-lg font-semibold mb-2 dark:text-white">No reports found</h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">All messages are clean! No reports to review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="dark:bg-gray-800 dark:border-gray-700">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-semibold text-sm sm:text-base dark:text-white">Reported by:</span>
                      <span className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{report.reported_by}</span>
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 text-xs rounded-full">
                        {report.reason}
                      </span>
                    </div>
                    
                    <div className="mb-2 text-sm sm:text-base">
                      <span className="font-semibold dark:text-white">From:</span>
                      <span className="text-gray-600 dark:text-gray-400 ml-2">{report.reported_user_name}</span>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-3">
                      <p className="text-xs sm:text-sm font-semibold mb-1 dark:text-gray-300">Message Content:</p>
                      <p className="text-gray-700 dark:text-gray-400 text-xs sm:text-sm break-words">{report.message_content}</p>
                    </div>
                    
                    {report.description && (
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                        <span className="font-semibold dark:text-gray-300">Description:</span> {report.description}
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Reported: {new Date(report.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => handleResolve(report.id, 'resolved')}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base touch-manipulation"
                    >
                      Resolve
                    </button>
                    <button
                      onClick={() => handleResolve(report.id, 'dismissed')}
                      className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base touch-manipulation"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboardNew;
